"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { RelatableVideoType } from "@/type/commonTypes";
import {
  getRelativeTime,
  getYoutubeThumbnail,
  getYTVideoId,
  formatTime,
} from "@/utils/commonFunctions";

import Icon from "../icons/Icons";
import CustomModal from "../modal/CustomModal";
import { useDispatch } from "react-redux";
import { openYTVideoModal, setVideoDataYTVideoModal } from "@/redux/features/ytVideoModal/ytVideoModalSlice";

type Props = {
  video: RelatableVideoType;
};

export default function RelatedSmallVideoCards({ video }: Props) {
  const dispatch = useDispatch();

  const thumbnail = useMemo(
    () => getYoutubeThumbnail(video.video_url ?? "") || "/images/default-thumbnail.jpg",
    [video.video_url],
  );

  const relativeTime = useMemo(() => getRelativeTime(String(video.created_at ?? "")), [video.created_at]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          dispatch(openYTVideoModal());
          dispatch(setVideoDataYTVideoModal(video));
        }}
        className="group block mb-6 sm:mb-5 xl:mb-9 last:mb-0 overflow-hidden"
      >
        <div className="flex gap-3">
          <div className="relative w-28 md:w-29.25 h-22.75 sm:h-20 xl:h-22.75 rounded-xl shrink-0 overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center z-1 bg-black/20 backdrop-blur-[1px]">
                  <Icon.ytBtnIcon className="w-8 h-auto" />
                </div>

            <Image
              src={thumbnail}
              alt={video.title ?? "Video Thumbnail"}
              fill
              className="rounded-xl object-cover object-left transition-transform duration-350 group-hover:scale-[1.03]"
            />
          </div>

          <div className="flex flex-col justify-center gap-2 md:gap-0 md:justify-between">
            <p className="text-2sm leading-1.2 xl:text-base xl:leading-6 font-semibold text-black text-left line-clamp-3 transition group-hover:text-primaryBlue">
              {video.title}
            </p>

            <ul className="mt-auto flex gap-2 text-xs sm:text-sm font-medium sm:font-bold text-[#6E6E73]">
              <li className="flex items-center gap-1">
                <Icon.durationIcon />
                {formatTime(video.duration ?? "")}
              </li>

              <li className="flex items-center gap-2 before:content-['•'] before:text-[#6E6E73]">{relativeTime}</li>
            </ul>
          </div>
        </div>
      </button>
    </>
  );
}
